<template>
  <div class="export-task-list-page">
    <div class="page-container">
      <!-- 页面头部 -->
      <div class="page-header">
        <button @click="goBack" class="btn btn-back">
          ← 返回
        </button>
        <h2>导出任务列表</h2>
      </div>

      <!-- 重要提示 -->
      <div class="info-banner">
        <span class="banner-icon">ℹ️</span>
        <span class="banner-text">提示：导出的文件仅保留15小时，请及时下载！</span>
      </div>

      <!-- 验证码输入区域 -->
      <section class="verification-section">
      <h3>查询任务</h3>
      <div class="verification-input-section">
        <div class="form-group">
          <label>卡密/激活码</label>
          <input
            v-model="verificationCode"
            class="form-control"
            type="text"
            placeholder="请输入卡密/激活码"
          />
        </div>
        <button
          @click="fetchTaskList"
          :disabled="loading || !verificationCode.trim()"
          class="btn btn-primary"
        >
          {{ loading ? '查询中...' : '查询任务列表' }}
        </button>
      </div>
    </section>

    <!-- 任务列表 -->
    <section class="activation-records-section" v-if="tasks.length > 0">
      <h3>任务列表</h3>

      <!-- 移动端卡片式布局 -->
      <div class="mobile-records" v-if="isMobile">
        <div v-if="loading" class="mobile-loading">
          正在加载任务数据...
        </div>
        <div v-else-if="tasks.length === 0" class="mobile-empty">
          <p>暂无任务数据</p>
        </div>
        <div v-else class="mobile-record-list">
          <div v-for="(task, index) in tasks" :key="task.taskId || index" class="mobile-record-item">
            <div class="mobile-record-header">
              <div class="record-title">任务 {{ formatTaskId(task.taskId) }}</div>
              <span class="status-badge" :class="getTaskStatusClass(task.status)">
                {{ getTaskStatusText(task.status) }}
              </span>
            </div>
            <div class="mobile-record-content">
              <div class="mobile-record-row">
                <span class="label">创建时间:</span>
                <span>{{ formatDateTime(task.createTime) }}</span>
              </div>
              <div class="mobile-record-row">
                <span class="label">导出格式:</span>
                <span>{{ task.format }}</span>
              </div>
              <div class="mobile-record-row">
                <span class="label">分辨率:</span>
                <span>{{ task.resolution }} DPI</span>
              </div>
              <div class="mobile-record-row" v-if="task.fileName">
                <span class="label">文件名:</span>
                <span>{{ task.fileName }}</span>
              </div>
              <div class="mobile-record-row" v-if="task.fileSize">
                <span class="label">文件大小:</span>
                <span>{{ task.fileSize }}</span>
              </div>
              <!-- 错误详情已隐藏，避免泄露后端错误信息 -->
            </div>
            <div class="mobile-record-actions">
              <button
                v-if="task.status === 'completed'"
                class="btn btn-primary"
                @click="downloadTaskFile(task)"
                :disabled="downloading"
              >
                {{ downloading ? '下载中...' : '下载文件' }}
              </button>
              <button
                v-else-if="task.status === 'pending' || task.status === 'processing'"
                class="btn btn-secondary"
                @click="checkTaskStatus(task.taskId)"
              >
                刷新状态
              </button>
              <button
                v-else-if="task.status === 'failed' && isPermissionError(task)"
                class="btn btn-danger"
                @click="showFailureDetail(task)"
              >
                查看失败原因
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 桌面端表格布局 -->
      <AdminTable
        v-else
        :columns="taskColumns"
        :data="tasks"
        :row-key="(task, index) => task.taskId || index"
        :max-height="tableMaxHeight"
      >
        <template #status="{ item }">
          <span class="status-badge" :class="getTaskStatusClass(item.status)">
            {{ getTaskStatusText(item.status) }}
          </span>
        </template>

        <template #createTime="{ item }">
          {{ formatDateTime(item.createTime) }}
        </template>

        <template #format="{ item }">
          {{ item.format }}
        </template>

        <template #resolution="{ item }">
          {{ item.resolution }} DPI
        </template>

        <template #taskId="{ item }">
          <span class="task-id">{{ formatTaskId(item.taskId) }}</span>
        </template>

        <template #fileName="{ item }">
          <code class="file-name" v-if="item.fileName" :title="item.fileName">{{ truncateFileName(item.fileName, 30) }}</code>
          <span v-else>-</span>
        </template>

        <template #fileSize="{ item }">
          {{ item.fileSize || '-' }}
        </template>

        <template #actions="{ item }">
          <button
            v-if="item.status === 'completed'"
            class="btn btn-primary btn-sm"
            @click="downloadTaskFile(item)"
            :disabled="downloading"
          >
            {{ downloading ? '下载中...' : '下载' }}
          </button>
          <button
            v-else-if="item.status === 'pending' || item.status === 'processing'"
            class="btn btn-secondary btn-sm"
            @click="checkTaskStatus(item.taskId)"
          >
            刷新
          </button>
          <button
            v-else-if="item.status === 'failed' && isPermissionError(item)"
            class="btn btn-danger btn-sm"
            @click="showFailureDetail(item)"
          >
            查看原因
          </button>
          <span v-else>-</span>
        </template>
      </AdminTable>
    </section>

    <!-- 空状态 -->
    <section class="no-task-info" v-else-if="!loading && tasks.length === 0 && verificationCode">
      <h3>任务列表</h3>
      <div class="no-task-card">
        <p class="no-task-text">暂无任务记录数据</p>
      </div>
    </section>

      <div v-if="message" class="message" :class="messageType">
        {{ message }}
      </div>
    </div>

    <!-- 错误结果弹窗 -->
    <div v-if="showResultModal" class="modal-overlay">
      <div class="modal result-modal">
        <div class="modal-body">
          <div class="result-content">
            <p class="result-text">{{ taskResult }}</p>
            <div v-if="screenshotUrl" class="screenshot-container">
              <img :src="screenshotUrl" alt="错误截图" class="screenshot" />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="confirm-btn" @click="closeResultModal">确定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AdminTable from '@/components/admin/AdminTable.vue'

export default {
  name: 'ExportTaskList',
  components: {
    AdminTable
  },
  data() {
    return {
      verificationCode: '',
      tasks: [],
      loading: false,
      downloading: false,
      isMobile: false,
      message: '',
      messageType: 'success',
      tableMaxHeight: 'none', // 默认不设置最大高度，让表格自适应显示所有内容
      showResultModal: false,
      taskResult: '',
      screenshotUrl: '',
      pollTimer: null, // 轮询定时器
      pollInterval: 3000, // 轮询间隔3秒（与导出页面保持一致）
      taskColumns: [
        { key: 'taskId', title: '任务ID' },
        { key: 'status', title: '状态' },
        { key: 'createTime', title: '创建时间' },
        { key: 'format', title: '格式' },
        { key: 'resolution', title: '分辨率' },
        { key: 'fileName', title: '文件名' },
        { key: 'fileSize', title: '大小' },
        { key: 'actions', title: '操作' }
      ]
    }
  },
  mounted() {
    this.checkMobile();
    this.updateTableMaxHeight(); // 初始化表格最大高度
    window.addEventListener('resize', this.checkMobile);
    window.addEventListener('resize', this.updateTableMaxHeight);


    // 从路由参数获取验证码
    const codeFromQuery = this.$route.query.code;
    if (codeFromQuery) {
      this.verificationCode = codeFromQuery;
      // 自动执行查询
      this.$nextTick(() => {
        this.fetchTaskList();
      });
    }

    // 轮询将在成功获取任务列表后按需启动（仅存在待处理或处理中的任务时）
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);
    window.removeEventListener('resize', this.updateTableMaxHeight);
    // 确保在组件销毁时停止轮询
    this.stopPolling();
  },
  methods: {
    goBack() {
      this.$router.push('/');
    },

    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },

    async fetchTaskList() {
      if (!this.verificationCode.trim()) {
        this.showMessage('请输入卡密/激活码', 'error');
        return;
      }

      this.loading = true;
      
      try {
        const response = await fetch('/api/export/tasks', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            verificationCode: this.verificationCode.trim()
          })
        });

        const result = await response.json();

        if (result.success) {
          this.tasks = result.data || [];

          // 根据任务列表状态决定是否启动轮询
          const hasPendingTasks = this.tasks.some(task => 
            task.status === 'pending' || task.status === 'processing'
          );
          
          if (hasPendingTasks && !this.pollTimer) {
            this.startPolling();
          } else if (!hasPendingTasks && this.pollTimer) {
            this.stopPolling();
          }
        } else {
          this.showMessage('获取任务列表失败', 'error');
          this.tasks = [];
          // 如果获取任务列表失败，停止轮询
          this.stopPolling();
        }
      } catch (error) {
        console.error('获取任务列表失败:', error);
        this.showMessage('网络错误，请稍后重试', 'error');
        this.tasks = [];
      } finally {
        this.loading = false;
      }
    },

    async checkTaskStatus(taskId) {
      try {
        const response = await fetch(`/api/export/status/${taskId}`);
        const result = await response.json();

        if (result.success) {
          // 更新对应任务的状态
          const taskIndex = this.tasks.findIndex(task => task.taskId === taskId);
          if (taskIndex !== -1) {
            this.tasks[taskIndex].status = result.status;
            if (result.fileName) this.tasks[taskIndex].fileName = result.fileName;
            if (result.fileSize) this.tasks[taskIndex].fileSize = result.fileSize;
            if (result.downloadUrl) this.tasks[taskIndex].downloadUrl = result.downloadUrl;
            if (result.error) this.tasks[taskIndex].errorMessage = result.error;
          }

          // 如果任务状态变为完成或失败，重新查询整个列表以保持一致性
          if (result.status === 'completed' || result.status === 'failed') {
            await this.fetchTaskList();
          } else {
            // 如果状态仍然是pending或processing，确保轮询是开启的
            const hasPendingTasks = this.tasks.some(task => 
              task.status === 'pending' || task.status === 'processing'
            );
            
            if (hasPendingTasks && !this.pollTimer) {
              this.startPolling();
            }
          }
        } else {
          this.showMessage('更新任务状态失败', 'error');
        }
      } catch (error) {
        console.error('更新任务状态失败:', error);
        this.showMessage('更新任务状态失败', 'error');
      }
    },

    async downloadTaskFile(task) {
      if (!task.downloadUrl) {
        this.showMessage('下载链接不可用', 'error');
        return;
      }

      this.downloading = true;
      try {
        // 构建完整的下载URL
        let fullDownloadUrl = task.downloadUrl;
        if (!fullDownloadUrl.startsWith('http')) {
          const baseUrl = window.location.origin;
          fullDownloadUrl = baseUrl + task.downloadUrl;
        }

        console.log('开始下载文件:', fullDownloadUrl);

        // 使用fetch获取blob数据
        const response = await fetch(fullDownloadUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/octet-stream',
            'Cache-Control': 'no-cache'
          }
        });

        // 检查响应状态
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        // 获取文件名（优先从响应头获取）
        let downloadFileName = task.fileName;
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
          // 尝试从Content-Disposition头部提取文件名
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            downloadFileName = filenameMatch[1].replace(/['"]/g, '');
          }

          // 处理UTF-8编码的文件名
          const utf8FilenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
          if (utf8FilenameMatch && utf8FilenameMatch[1]) {
            downloadFileName = decodeURIComponent(utf8FilenameMatch[1]);
          }
        }

        // 获取blob对象
        const blob = await response.blob();
        console.log('文件blob获取成功, 大小:', blob.size, 'bytes');

        // 创建blob URL
        const blobUrl = window.URL.createObjectURL(blob);

        // 创建下载链接并触发下载
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = downloadFileName || 'download';
        link.style.display = 'none';

        // 添加到DOM并点击
        document.body.appendChild(link);
        link.click();

        // 清理DOM和内存
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        console.log('下载成功:', downloadFileName);

      } catch (error) {
        console.error('下载失败:', error);

        // 根据错误类型提供更具体的错误信息
        let errorMessage = '下载失败，请重试';
        if (error.message.includes('404')) {
          errorMessage = '文件不存在或已过期';
        } else if (error.message.includes('403')) {
          errorMessage = '没有下载权限';
        } else if (error.message.includes('网络')) {
          errorMessage = '网络错误，请检查连接';
        } else if (error.message.includes('HTTP')) {
          errorMessage = `服务器错误：${error.message}`;
        }

        this.showMessage(errorMessage, 'error');
      } finally {
        this.downloading = false;
      }
    },

    getTaskStatusText(status) {
      const statusMap = {
        'pending': '待处理',
        'processing': '处理中',
        'completed': '已完成',
        'failed': '失败'
      };
      return statusMap[status] || status;
    },

    getTaskStatusClass(status) {
      const statusClassMap = {
        'pending': 'status-pending',
        'processing': 'status-processing',
        'completed': 'status-completed',
        'failed': 'status-failed'
      };
      return statusClassMap[status] || 'status-pending';
    },

    formatDateTime(dateTimeStr) {
      if (!dateTimeStr) return '-';
      try {
        const date = new Date(dateTimeStr);
        return date.toLocaleString('zh-CN');
      } catch (e) {
        return dateTimeStr;
      }
    },

    truncateFileName(fileName, maxLength = 30) {
      if (!fileName || fileName.length <= maxLength) {
        return fileName;
      }
      // 分离文件名和扩展名
      const lastDotIndex = fileName.lastIndexOf('.');
      if (lastDotIndex === -1) {
        // 没有扩展名，直接截断
        return fileName.substring(0, maxLength - 3) + '...';
      }
      
      const namePart = fileName.substring(0, lastDotIndex);
      const extensionPart = fileName.substring(lastDotIndex);
      
      // 保留扩展名，截断文件名部分
      if (namePart.length > maxLength - extensionPart.length - 3) {
        return namePart.substring(0, maxLength - extensionPart.length - 3) + '...' + extensionPart;
      }
      
      return fileName;
    },

    showMessage(text, type = 'success') {
      this.message = text;
      this.messageType = type === 'error' ? 'error' : 'success';
      setTimeout(() => {
        this.message = '';
      }, 3000);
    },

    updateTableMaxHeight() {
      // 计算可用的视窗高度，减去页面其他部分的高度
      if (this.isMobile) {
        // 移动端不需要设置最大高度，直接使用'none'
        this.tableMaxHeight = 'none';
        return;
      }

      // 获取页面容器高度和其他元素的高度
      const windowHeight = window.innerHeight;
      const headerHeight = 180; // 页面头部、提示、查询区域等的高度估算
      const footerPadding = 100; // 页面底部留白
      const availableHeight = windowHeight - headerHeight - footerPadding;

      // 如果可用高度大于一定值（如500px），则使用动态计算的高度，否则设置一个最小值
      if (availableHeight > 300) {
        this.tableMaxHeight = `${availableHeight}px`;
      } else {
        this.tableMaxHeight = '400px'; // 最小高度
      }
    },

    formatTaskId(taskId) {
      if (!taskId) return '-';
      // 显示任务ID的最后8位，前面用...表示
      if (taskId.length > 8) {
        return '...' + taskId.slice(-8);
      }
      return taskId;
    },

    closeResultModal() {
      this.showResultModal = false;
      this.taskResult = '';
      this.screenshotUrl = '';
    },

    showFailureDetail() {
      // 显示权限错误弹窗（只有 permission_denied 类型才会调用此方法）
      this.taskResult = '文件无权限访问，请修改文件权限为可编辑';
      this.screenshotUrl = require('@/assets/img.png');
      this.showResultModal = true;
    },

    isPermissionError(task) {
      // 通过 errorMessage 判断是否为权限错误
      if (!task || !task.errorMessage) {
        return false;
      }
      const errorMsg = task.errorMessage.toLowerCase();
      return errorMsg.includes('无权限访问') ||
             errorMsg.includes('permission') ||
             errorMsg.includes('文件权限') ||
             errorMsg.includes('file access denied');
    },

    startPolling() {
      // 清除现有的轮询定时器
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }

      // 设置新的轮询定时器
      this.pollTimer = setInterval(() => {
        this.checkAllPendingTasks();
      }, this.pollInterval);
    },

    async checkAllPendingTasks() {
      // 查找所有待处理或处理中的任务
      const pendingTasks = this.tasks.filter(task => 
        task.status === 'pending' || task.status === 'processing'
      );

      if (pendingTasks.length === 0) {
        // 如果没有待处理任务，停止轮询
        this.stopPolling();
        return;
      }

      // 为每个待处理任务检查状态
      for (const task of pendingTasks) {
        try {
          const response = await fetch(`/api/export/status/${task.taskId}`);
          const result = await response.json();

          if (result.success) {
            // 更新任务状态
            const taskIndex = this.tasks.findIndex(t => t.taskId === task.taskId);
            if (taskIndex !== -1) {
              this.tasks[taskIndex].status = result.status;
              if (result.fileName) this.tasks[taskIndex].fileName = result.fileName;
              if (result.fileSize) this.tasks[taskIndex].fileSize = result.fileSize;
              if (result.downloadUrl) this.tasks[taskIndex].downloadUrl = result.downloadUrl;
              if (result.message) this.tasks[taskIndex].message = result.message;
              if (result.error) this.tasks[taskIndex].errorMessage = result.error;
            }

            // 如果任务状态变为完成或失败，刷新整个列表保持一致性
            if (result.status === 'completed' || result.status === 'failed') {
              await this.fetchTaskList();
            }
          }
        } catch (error) {
          console.error('检查任务状态失败:', error);
        }
      }
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    }
  }
}
</script>

<style scoped>
@import '@/styles/admin-common.css';

/* 页面整体容器 */
.export-task-list-page {
  min-height: 100vh;
  background: #f5f7fa;
  padding: var(--spacing-xl);
}

.page-container {
  max-width: 1200px;
  margin: 0 auto;
  background: var(--bg-white);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xxl);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

/* 页面头部 */
.page-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xxl);
  padding-bottom: var(--spacing-lg);
  border-bottom: 2px solid var(--border-color);
}

.page-header h2 {
  flex: 1;
  text-align: center;
  font-size: var(--font-size-xxl);
  margin: 0;
  color: var(--text-primary);
  font-weight: 600;
}

.btn-back {
  background: var(--bg-light);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-md);
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
}

.btn-back:hover {
  background: var(--primary-color, #2176c7);
  color: white;
  border-color: var(--primary-color, #2176c7);
  transform: translateX(-2px);
  box-shadow: 0 2px 8px rgba(33, 118, 199, 0.3);
}

/* 信息提示横幅 */
.info-banner {
  background: linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%);
  border: 2px solid #4dd0e1;
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-md) var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  box-shadow: 0 2px 8px rgba(77, 208, 225, 0.2);
  animation: fadeIn 0.3s ease;
}

.banner-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.banner-text {
  color: #006064;
  font-weight: 600;
  font-size: var(--font-size-md);
  flex: 1;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

h3 {
  font-size: var(--font-size-xl);
  border-bottom: 2px solid var(--border-color);
  padding-bottom: var(--spacing-sm);
  margin-bottom: var(--spacing-xl);
  color: var(--text-primary);
}

.verification-section {
  background: var(--bg-light);
  padding: var(--spacing-xl);
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--border-color);
  margin-bottom: var(--spacing-xxl);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.verification-input-section {
  display: flex;
  align-items: flex-end;
  gap: var(--spacing-md);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  flex: 1;
  max-width: 500px;
}

.form-group label {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  font-weight: 600;
  margin-bottom: 0;
}

.form-group .form-control {
  width: 100%;
}

/* 桌面端表格布局 */
.activation-records-section {
  margin-bottom: var(--spacing-xxl);
  background: var(--bg-white);
  padding: var(--spacing-xl);
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.admin-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: var(--spacing-md);
  table-layout: fixed; /* 固定表格布局，防止列宽自动调整 */
}

.admin-table th,
.admin-table td {
  padding: var(--spacing-md);
  text-align: left;
  border-bottom: 1px solid var(--border-color);
  word-wrap: break-word; /* 允许长内容换行 */
  overflow: hidden;
  text-overflow: ellipsis;
}

.admin-table th {
  background: var(--bg-light);
  font-weight: 600;
  color: var(--text-primary);
  border-top: 1px solid var(--border-color);
}

.admin-table tr:last-child td {
  border-bottom: none;
}

/* 特定列的宽度设置 */
.admin-table th:nth-child(1), .admin-table td:nth-child(1) { /* 任务ID */
  width: 8%;
}

.admin-table th:nth-child(2), .admin-table td:nth-child(2) { /* 状态 */
  width: 10%;
}

.admin-table th:nth-child(3), .admin-table td:nth-child(3) { /* 创建时间 */
  width: 15%;
}

.admin-table th:nth-child(4), .admin-table td:nth-child(4) { /* 格式 */
  width: 8%;
}

.admin-table th:nth-child(5), .admin-table td:nth-child(5) { /* 分辨率 */
  width: 8%;
}

.admin-table th:nth-child(6), .admin-table td:nth-child(6) { /* 文件名 */
  width: 15%;
}

.admin-table th:nth-child(7), .admin-table td:nth-child(7) { /* 大小 */
  width: 8%;
}

.admin-table th:nth-child(8), .admin-table td:nth-child(8) { /* 操作 */
  width: 12%;
}

/* 任务ID样式 */
.task-id {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  background: var(--bg-light);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--border-light);
  display: inline-block;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 卡密代码样式 */
.card-code,
.file-name {
  background: var(--bg-primary);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: var(--font-size-sm);
  color: var(--primary-color);
  border: 1px solid var(--border-light);
  display: inline-block;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 移动端激活记录样式 */
.mobile-records {
  display: block;
}

.mobile-loading, .mobile-empty {
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--text-secondary);
  font-style: italic;
}

.mobile-record-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.mobile-record-item {
  background: var(--bg-light);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.mobile-record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-sm);
  padding-bottom: var(--spacing-sm);
  border-bottom: 1px solid var(--border-light);
}

.record-title {
  font-weight: 600;
  color: var(--text-primary);
  font-size: var(--font-size-md);
}

.mobile-record-content {
  margin-bottom: var(--spacing-md);
}

.mobile-record-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-xs) 0;
  border-bottom: 1px solid var(--border-lighter);
}

.mobile-record-row:last-child {
  border-bottom: none;
}

.mobile-record-row .label {
  font-weight: 600;
  color: var(--text-secondary);
  flex: 0 0 auto;
  min-width: 100px;
}

.mobile-record-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: var(--spacing-sm);
}

.mobile-record-row.error {
  color: var(--error-color);
}

/* 空状态样式 */
.no-task-info {
  margin-bottom: var(--spacing-xxl);
}

.no-task-card {
  text-align: center;
  background: var(--bg-light);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xxl);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.no-task-text {
  color: var(--text-secondary);
  font-size: var(--font-size-lg);
  margin: 0;
}

/* 消息提示样式 */
.message {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  max-width: 90%;
  padding: var(--spacing-md) var(--spacing-xl);
  border-radius: var(--border-radius-md);
  font-weight: 500;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  animation: slideInDown 0.3s ease;
}

.message.success {
  background: var(--success-bg);
  color: var(--success-color);
  border: 1px solid var(--success-border);
}

.message.error {
  background: var(--error-bg);
  color: var(--error-color);
  border: 1px solid var(--error-border);
}

@keyframes slideInDown {
  from {
    transform: translate(-50%, -50px);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
}

/* 按钮样式 */
.btn {
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px solid transparent;
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-sm {
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: var(--font-size-sm);
}

.btn-primary {
  background: var(--primary-color, #2176c7);
  color: white;
  border-color: var(--primary-color, #2176c7);
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-color-dark, #1a5f9e);
  border-color: var(--primary-color-dark, #1a5f9e);
  color: white;
  box-shadow: 0 4px 12px rgba(33, 118, 199, 0.4);
  transform: translateY(-1px);
}

.btn-secondary {
  background: var(--bg-light, #f8f9fa);
  color: var(--text-primary, #212529);
  border-color: var(--border-color, #dee2e6);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--bg-primary, #e9ecef);
  border-color: var(--border-color-dark, #adb5bd);
  color: var(--text-primary, #212529);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.btn-danger {
  background: #dc3545;
  color: white;
  border-color: #dc3545;
}

.btn-danger:hover:not(:disabled) {
  background: #c82333;
  border-color: #bd2130;
  color: white;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4);
  transform: translateY(-1px);
}

/* 状态徽章样式 */
.status-badge {
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-weight: 600;
  text-transform: uppercase;
  display: inline-block;
  min-width: 60px;
  text-align: center;
}

.status-pending {
  background: #fff3cd;
  color: #856404;
}

.status-processing {
  background: #cce5ff;
  color: #0066cc;
}

.status-completed {
  background: #d4edda;
  color: #155724;
}

.status-failed {
  background: #f8d7da;
  color: #721c24;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .export-task-list-page {
    padding: var(--spacing-md);
  }

  .page-container {
    padding: var(--spacing-lg);
    border-radius: var(--border-radius-md);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-md);
  }

  .page-header h2 {
    text-align: left;
    font-size: var(--font-size-xl);
    width: 100%;
  }

  .btn-back {
    width: 100%;
    justify-content: center;
    order: -1;
  }

  .info-banner {
    padding: var(--spacing-sm) var(--spacing-md);
    flex-direction: column;
    text-align: center;
    gap: var(--spacing-xs);
  }

  .banner-text {
    font-size: var(--font-size-sm);
  }

  .verification-section {
    padding: var(--spacing-lg);
  }

  .verification-input-section {
    flex-direction: column;
    align-items: stretch;
  }

  .form-group {
    max-width: 100% !important;
  }

  .admin-table {
    display: none;
  }

  .mobile-records {
    display: block;
  }

  .mobile-record-row .label {
    min-width: 80px;
  }

  .btn {
    width: 100%;
    margin-top: var(--spacing-sm);
  }

  .mobile-record-actions {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  .activation-records-section {
    padding: var(--spacing-lg);
  }
}

@media (max-width: 480px) {
  .page-container {
    padding: var(--spacing-md);
  }

  .page-header h2 {
    font-size: var(--font-size-lg);
  }

  .verification-section,
  .activation-records-section {
    padding: var(--spacing-md);
  }

  h3 {
    font-size: var(--font-size-lg);
  }

  .mobile-record-item {
    padding: var(--spacing-sm);
  }

  .mobile-record-row .label {
    min-width: 70px;
    font-size: var(--font-size-sm);
  }

  .mobile-record-row {
    font-size: var(--font-size-sm);
  }
}

/* 错误结果弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal.result-modal {
  background: white;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-body {
  padding: var(--spacing-xl);
}

.result-content {
  text-align: center;
}

.result-text {
  margin: 0 0 var(--spacing-lg) 0;
  line-height: 1.6;
  color: var(--text-primary);
}

.screenshot-container {
  margin: var(--spacing-md) 0;
}

.screenshot {
  max-width: 100%;
  max-height: 300px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
}

.modal-footer {
  padding: var(--spacing-lg) var(--spacing-xl);
  border-top: 1px solid var(--border-color);
  text-align: center;
}

.confirm-btn {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: var(--spacing-sm) var(--spacing-xl);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  font-size: var(--font-size-md);
  font-weight: 500;
}

.confirm-btn:hover {
  background: var(--primary-color-dark, #1a5f9e);
  color: white;
}
</style>